import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import usePageMeta from "@/hooks/usePageMeta";
import PageHeader from "@/components/PageHeader";
import { Reveal, Overline } from "@/components/Reveal";
import { MANIFESTO, STATS, HERO_IMAGE, MANIFESTO_IMAGE } from "@/lib/data";
import { SITE } from "@/lib/site";
import { OfficeGallery } from "@/components/OfficeGallery";

export default function About() {
  usePageMeta({
    title: "About TripTrix Voyages — a Ground DMC in North East India",
    description:
      "A North East India ground DMC for the travel trade since 2016 — offices in Siliguri & Guwahati, GST-registered & Udyam/MSME. Meet your ground team.",
  });
  return (
    <>
      <PageHeader
        overline="About TripTrix Voyages"
        title="A ground DMC, rooted in the North East since 2016"
        sub="We work only with travel agents and tour operators — handling permits, transport, stays and guides on the ground so you can sell the North East, Sikkim, Bhutan, Nepal and Darjeeling with confidence."
        image={HERO_IMAGE}
      />

      <section className="py-4 md:py-8" data-testid="about-intro">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 grid gap-12 lg:grid-cols-12 items-start">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="text-sm uppercase tracking-[0.2em] text-[var(--tx-gold)] mb-4" data-testid="about-parent-company">
                A unit of {SITE.parentCompany}
              </p>
              <p className="font-display text-3xl md:text-4xl font-light leading-snug">
                We are a <span className="text-[var(--tx-gold)]">B2B-only</span> ground Destination
                Management Company — we work exclusively with travel agents & tour operators, not
                direct travellers. Every permit, guide, homestay and jeep is arranged by our own team on the ground.
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="mt-8 space-y-5 text-lg text-[var(--tx-text-2)] leading-relaxed">
                <p>
                  Founded in Siliguri (Shiv Mandir), West Bengal and operating across all eight North Eastern states,
                  Bhutan, Nepal and the Darjeeling hills, TripTrix exists to make the most
                  remote corners of the Himalaya feel effortless, safe and profoundly personal.
                </p>
                <p>
                  We handle the inner-line permits, the last-mile logistics and the
                  hard-to-find local connections — so you can sell with confidence.
                  A single coordinator stays with you from your first message to your
                  travellers&apos; return.
                </p>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.15}>
              <div className="relative h-[420px] overflow-hidden">
                <img src={MANIFESTO_IMAGE} alt="Himalayan peaks" loading="lazy" decoding="async" className="h-full w-full object-cover" />
                <div className="absolute inset-0 ring-1 ring-inset ring-white/10" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 grid grid-cols-2 md:grid-cols-4 gap-8 border-y border-[var(--tx-border)] py-14">
          {STATS.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.08}>
              <div className="font-display text-4xl md:text-5xl font-light text-[var(--tx-gold)]">{s.value}</div>
              <div className="mt-2 text-xs uppercase tracking-[0.18em] text-[var(--tx-text-2)]">{s.label}</div>
            </Reveal>
          ))}
        </div>
      </section>

      <OfficeGallery />

      <section className="pb-28" data-testid="about-values">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <Reveal><Overline>What we stand for</Overline></Reveal>
          <div className="mt-12 space-y-0">
            {MANIFESTO.map((m, i) => (
              <Reveal key={m.no} delay={i * 0.05}>
                <div className="grid grid-cols-1 md:grid-cols-12 gap-6 py-9 border-t border-[var(--tx-border)]">
                  <div className="md:col-span-2 font-display text-4xl md:text-5xl font-light text-[var(--tx-gold)]">{m.no}</div>
                  <h3 className="md:col-span-4 font-display text-2xl md:text-3xl font-light">{m.title}</h3>
                  <p className="md:col-span-6 text-[var(--tx-text-2)] leading-relaxed text-lg">{m.body}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal>
            <div className="mt-16 flex flex-col sm:flex-row items-center justify-between gap-6 border border-[var(--tx-border)] bg-[var(--tx-bg-2)] p-10">
              <h3 className="font-display text-3xl font-light">Send us your query — we&apos;ll come back with net rates.</h3>
              <Link to="/contact" className="inline-flex items-center gap-2 rounded-full bg-[var(--tx-gold)] px-8 py-3.5 text-sm font-medium text-[var(--tx-bg)] hover:bg-[var(--tx-text)] transition-colors duration-300">
                Get net rates <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
